'use client';

import { useTranslations } from 'next-intl';
import { type FC, useEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { ProductLineUserGuide } from '@/app/components/icons/ProductLineUserGuide';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { Button } from '@/app/components/ui/button';
import { ProgressiveBlur } from '@/app/components/ui/progressive-blur';
import { useToolContainerOverlayPosition } from '@/app/hooks';
import type { FormTabType, SupportedLanguage } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { EN_GUIDE_URL, ZH_GUIDE_URL } from '../constants';

export type Tutorial = {
  videoUrl: Partial<Record<SupportedLanguage, string>>;
  posterUrl: Partial<Record<SupportedLanguage, string>>;
  thumbnailUrl?: Partial<Record<SupportedLanguage, string>>;
  exampleIndex?: number;
  type?: 'tutorial' | 'example';
};

interface TutorialPanelProps {
  open: boolean;
  onClose: () => void;
  onClickExample: (exampleIdx: number) => void;
  tutorials: Tutorial[];
  type: Extract<FormTabType, 'img2video' | 'character2video'>;
  showMask?: boolean;
}

export const TutorialPanel: FC<TutorialPanelProps> = ({
  open,
  onClose,
  tutorials,
  onClickExample,
  type,
  showMask = false,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectedTutorial, setSelectedTutorial] = useState(tutorials[0]);
  const overlayPosition = useToolContainerOverlayPosition({
    open,
    sideOffset: 8,
    viewportPadding: 24,
  });

  const getLocalizedUrl = (urlObject: Record<string, string> | undefined) => {
    if (!urlObject) return undefined;
    return urlObject[locale] ?? urlObject.en;
  };

  const localizedSelectedPosterUrl = getLocalizedUrl(selectedTutorial.posterUrl);
  const desktopPanelWidth = Math.min(640, Math.max(360, overlayPosition.availableWidth));
  const desktopPanelLeft = Math.min(
    overlayPosition.left,
    overlayPosition.left + overlayPosition.availableWidth - desktopPanelWidth,
  );
  const desktopPanelStyle = showMask
    ? undefined
    : {
        top: `${overlayPosition.top}px`,
        left: `${Math.max(24, desktopPanelLeft)}px`,
        width: `${desktopPanelWidth}px`,
        maxWidth: 'calc(100vw - 48px)',
      };

  // 点击外部 & ESC 键关闭面板
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !document.getElementById('user-guide-button')?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (open) {
      !showMask && document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      !showMask && document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose, open, showMask]);

  if (!open) {
    return null;
  }

  return (
    <>
      <div
        className={cn('fixed inset-0 z-[60] hidden bg-system-black10 backdrop-blur-sm', showMask && 'block')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="sm:-translate-y-1/2 sm:-translate-x-1/2 fixed z-[60] flex max-h-[85vh] flex-col overflow-y-auto rounded-12 border border-system-white12 bg-[#222830]/80 p-4 shadow-[0px_4px_100px_0px_rgba(0,0,0,0.64)] backdrop-blur-2xl sm:top-1/2 sm:left-1/2 sm:w-[calc(100vw-32px)] sm:max-w-[532px]"
        style={desktopPanelStyle}
      >
        <BaseLineClose onClick={onClose} className="absolute top-4 right-4 text-system-white text-xl" />

        <div className="flex select-none flex-col items-center">
          <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-system-white12 px-3 py-1 text-system-white text-xs">
            <ProductLineUserGuide className="text-sm" />
            <span>{t('create.tutorial.tutorial')}</span>
          </div>
          <h3 className="mt-2.5 mb-1.5 font-medium text-system-white text-xl leading-6 md:mt-2 md:mb-1 md:text-center md:text-xl md:leading-6">
            {t(`create.tutorial.${type}.title`)}
          </h3>
          <p className="whitespace-pre-wrap text-center text-system-white64 text-xs">
            {t.rich(`create.tutorial.${type}.description`, {
              a: (chunks) => (
                <ClientLink
                  href={locale === 'zh' ? ZH_GUIDE_URL : EN_GUIDE_URL}
                  className="ms-1 inline text-system-blue02 hover:text-system-blue01"
                  target="_blank"
                >
                  <ProductLineUserGuide className="me-1 inline text-sm" />
                  {chunks}
                </ClientLink>
              ),
            })}
          </p>
        </div>

        <div className="relative mt-5 aspect-video w-full flex-shrink-0 overflow-hidden rounded-8">
          <video
            src={getLocalizedUrl(selectedTutorial.videoUrl)}
            className="absolute inset-0 aspect-video w-full"
            autoPlay
            muted
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            poster={localizedSelectedPosterUrl}
            controls
            onEnded={() => {
              setSelectedTutorial(tutorials[(tutorials.indexOf(selectedTutorial) + 1) % tutorials.length]);
            }}
          />
        </div>

        <div className="mt-4 flex min-h-[68px] space-x-2 overflow-x-auto">
          {tutorials.map((tutorial) => (
            <div
              key={getLocalizedUrl(tutorial.posterUrl)}
              className={cn(
                'relative aspect-video min-w-[89px] flex-1 cursor-pointer overflow-hidden rounded-4 border-2 md:flex-shrink-0',
                localizedSelectedPosterUrl === getLocalizedUrl(tutorial.posterUrl)
                  ? 'border-system-blue02'
                  : 'border-transparent',
              )}
              onClick={() => setSelectedTutorial(tutorial)}
            >
              <ImageWithPlaceholder
                src={getLocalizedUrl(tutorial.thumbnailUrl) ?? getLocalizedUrl(tutorial.posterUrl)}
                alt=""
                className="size-full object-cover"
              />

              {tutorial.type === 'example' && (
                <>
                  <ProgressiveBlur className="absolute inset-0" direction="to bottom" blurIntensity={[0, 0, 2, 4]} />
                  <div className="absolute bottom-0 left-0 flex h-9 w-full items-center justify-center bg-gradient-to-t from-black to-black/0">
                    <Button
                      className="text-system-white text-xs hover:text-system-white64"
                      size="original"
                      disabled={tutorial.exampleIndex === undefined}
                      onClick={(e) => {
                        if (tutorial.exampleIndex !== undefined) {
                          e.stopPropagation();
                          onClickExample(tutorial.exampleIndex);
                          showMask && onClose();
                        }
                      }}
                    >
                      {t('create.tutorial.recreate')}
                    </Button>
                  </div>
                  <div className="absolute top-0.5 left-0.5 rounded-4 bg-system-black64 px-2 py-1 text-[10px] text-white leading-3 backdrop-blur-sm">
                    {t('create.tutorial.sample')}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
